import React from 'react';
import { fieldLabel, fillFieldControl, safeHref } from '../../lib/hostedForm';

// The supplier-facing renderer for one document block.
//
// Extracted so the full-page signer and anything else that shows live document
// content render from ONE implementation. When the signing view and the stored
// record disagree about how a document looks, the stored record is the one
// that matters and the supplier has signed something they didn't see — so
// these must be changed together with renderSubmissionHtml's markup.

const INK = '#2A2E24';

// Author-chosen size steps, resolved to the same values the document
// stylesheet uses. Keep in step with .sz-* in renderSubmissionHtml.
const sizeClass = (size) => (size === 'sm' ? 'text-[0.86em]' : size === 'lg' ? 'text-[1.18em]' : '');
const INDENT = ['', 'ml-[26px]', 'ml-[52px]', 'ml-[78px]'];
const headingWeight = (w) => (w === 'light' ? 200 : w === 'bold' ? 700 : 600);

export function FormBlock({ block: b, values, ticks, disabled, onFieldChange, onInputChange, onTick, onAttach, uploadingId }) {
  if (b.type === 'heading') {
    const Tag = b.level === 3 ? 'h4' : 'h3';
    return (
      <Tag
        className={`font-['Metropolis'] mt-6 first:mt-0 ${b.level === 3 ? 'text-[14px]' : 'text-[15px]'}`}
        style={{ color: b.level === 3 ? '#92D400' : '#808080', fontWeight: headingWeight(b.weight) }}>
        {b.text}
      </Tag>
    );
  }

  if (b.type === 'divider') {
    return <hr className="border-0 border-t border-[#D7E3C4] my-5" />;
  }

  if (b.type === 'table') {
    const rows = b.rows || [];
    if (!rows.length) return null;
    const hasHead = b.header !== false;
    const bodyRows = hasHead ? rows.slice(1) : rows;
    return (
      // The table scrolls inside its own box — a wide table must never make the
      // whole document scroll sideways on a phone.
      <div className="overflow-x-auto my-3">
        <table className="w-full border-collapse text-[14px]">
          {hasHead && (
            <thead>
              <tr>
                {rows[0].map((c, i) => (
                  <th key={i} className="border border-[#D7E3C4] bg-[#F4F7EC] px-[11px] py-2 text-left font-semibold align-top" style={{ color: INK }}>{c}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {bodyRows.map((r, ri) => (
              <tr key={ri}>
                {r.map((c, ci) => (
                  <td key={ci} className="border border-[#D7E3C4] px-[11px] py-2 align-top" style={{ color: INK }}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (b.type === 'bullet') {
    const items = (b.items || []).filter(it => String(it || '').trim());
    if (!items.length) return null;
    return (
      <ul className="font-['Metropolis'] text-[14px] leading-relaxed list-disc pl-6" style={{ color: INK }}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    );
  }

  if (b.type === 'paragraph') {
    const renderToken = (t, i) => {
      if (t.t === 'field') {
        return (
          <input
            key={i}
            value={values[t.source] ?? ''}
            onChange={e => onFieldChange(t.source, e.target.value)}
            disabled={disabled}
            title={`${fieldLabel(t.source)} — prefilled from your account, editable`}
            aria-label={fieldLabel(t.source)}
            className="inline-block align-baseline mx-0.5 px-1.5 py-0.5 bg-[#2E8FA1]/8 border border-[#2E8FA1]/30 rounded text-[13px] font-semibold text-[#2E8FA1] focus:outline-none focus:border-[#2E8FA1] min-w-[80px]"
            style={{ width: `${Math.max(8, String(values[t.source] || '').length + 1)}ch` }} />
        );
      }
      if (t.t === 'input') {
        const control = fillFieldControl(t.ftype);
        const label = `${t.label}${t.optional ? ' (optional)' : ''}`;
        const cls = 'inline-block align-baseline mx-0.5 px-1.5 py-0.5 bg-[#2F6E14]/8 border border-[#2F6E14]/30 rounded text-[13px] font-semibold text-[#2F6E14] focus:outline-none focus:border-[#2F6E14]';
        if (control === 'yesno') {
          return (
            <select key={i} value={values[t.id] ?? ''} onChange={e => onInputChange(t.id, e.target.value)} disabled={disabled} aria-label={label} className={cls}>
              <option value="">{t.optional ? '—' : 'Yes / No'}</option>
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          );
        }
        // ATTACHMENT — an answer that is a document rather than a sentence.
        // Renders as a chip in the run of text, so it stays attached to the
        // question that asked for it. Once a file is on, the chip becomes the
        // filename with a way to remove it: a supplier who cannot see what they
        // attached will attach it again.
        if (control === 'file') {
          const attached = values[t.id];
          const busy = uploadingId === t.id;
          const inputId = `hf-attach-${t.id}`;
          if (attached?.url) {
            return (
              <span key={i} className="inline-flex items-baseline gap-1.5 align-baseline mx-0.5 px-1.5 py-0.5 bg-[#2F6E14]/8 border border-[#2F6E14]/30 rounded text-[13px] font-semibold text-[#2F6E14] max-w-[280px]">
                <a href={attached.url} target="_blank" rel="noopener noreferrer" className="truncate hover:underline" title={attached.name}>
                  {attached.name || 'Attachment'}
                </a>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => onAttach?.(t.id, null)}
                    aria-label={`Remove ${attached.name || 'attachment'}`}
                    className="text-[#2F6E14]/70 hover:text-[#2F6E14] leading-none">×</button>
                )}
              </span>
            );
          }
          return (
            <span key={i} className="inline-block align-baseline mx-0.5">
              <input
                id={inputId}
                type="file"
                className="hidden"
                disabled={disabled || busy}
                onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onAttach?.(t.id, f); }} />
              <label
                htmlFor={inputId}
                title={label}
                className={`inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#2F6E14]/8 border border-[#2F6E14]/30 border-dashed rounded text-[13px] font-semibold text-[#2F6E14] ${disabled || busy ? 'opacity-50' : 'cursor-pointer hover:border-solid'}`}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                {busy ? 'Uploading…' : (t.label || 'Attach a file')}
              </label>
            </span>
          );
        }
        const type = control === 'date' ? 'date' : control === 'number' ? 'number' : control === 'email' ? 'email' : control === 'tel' ? 'tel' : 'text';
        return (
          <input
            key={i}
            type={type}
            value={values[t.id] ?? ''}
            onChange={e => onInputChange(t.id, e.target.value)}
            disabled={disabled}
            placeholder={t.label}
            aria-label={label}
            className={`${cls} placeholder:text-[#2F6E14]/50 placeholder:font-normal ${control === 'textarea' ? 'min-w-[180px]' : 'min-w-[90px]'}`}
            style={control === 'date' ? undefined : { width: `${Math.max(control === 'textarea' ? 18 : 9, String(values[t.id] || t.label || '').length + 1)}ch` }} />
        );
      }
      if (t.t === 'link') {
        const href = safeHref(t.href);
        // Same rule as the stored record: an unsafe href renders as plain text
        // rather than as a link somewhere unintended.
        return href
          ? <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="text-fusion-forest underline">{t.v}</a>
          : <span key={i}>{t.v}</span>;
      }
      let node = t.v;
      if (t.i) node = <em>{node}</em>;
      if (t.b) node = <strong style={{ color: '#808080' }}>{node}</strong>;
      if (t.u) node = <u>{node}</u>;
      return <span key={i} className={sizeClass(t.size)}>{node}</span>;
    };

    // Runs of list lines group into one list; a run ends when the marker kind
    // or the indent changes, so nesting reads as nesting.
    const lns = b.lines || [];
    const parts = [];
    let k = 0;
    while (k < lns.length) {
      const kind = lns[k].bullet ? 'bullet' : lns[k].numbered ? 'numbered' : null;
      const indent = Math.max(0, Math.min(3, Number(lns[k].indent) || 0));
      if (kind) {
        const group = [];
        while (k < lns.length) {
          const kk = lns[k].bullet ? 'bullet' : lns[k].numbered ? 'numbered' : null;
          const ii = Math.max(0, Math.min(3, Number(lns[k].indent) || 0));
          if (kk !== kind || ii !== indent) break;
          group.push(lns[k]); k += 1;
        }
        const ListTag = kind === 'numbered' ? 'ol' : 'ul';
        parts.push(
          <ListTag key={`l-${k}`} className={`${kind === 'numbered' ? 'list-decimal' : 'list-disc'} pl-6 my-1 font-['Metropolis'] ${INDENT[indent]}`} style={{ color: INK, lineHeight: 1.75 }}>
            {group.map((ln, gi) => <li key={gi} className="my-0.5">{(ln.tokens || []).map(renderToken)}</li>)}
          </ListTag>
        );
      } else {
        const ln = lns[k];
        parts.push(
          <p key={`p-${k}`} className={`font-['Metropolis'] ${INDENT[indent]}`} style={{ color: INK, lineHeight: 1.75 }}>
            {(ln.tokens || []).map(renderToken)}
          </p>
        );
        k += 1;
      }
    }
    return <div>{parts}</div>;
  }

  if (b.type === 'clause') {
    return (
      <label
        id={`clause-${b.id}`}
        className={`flex items-start gap-3 rounded-lg border border-l-[3px] px-4 py-3 my-2 transition-colors ${
          disabled ? 'cursor-default' : 'cursor-pointer'
        } ${ticks[b.id]
          ? 'border-[#2F6E14] border-l-[#2F6E14] bg-[#2F6E14]/[0.05]'
          : 'border-[#e6e9da] border-l-[#2F6E14] bg-[#fafbf6] hover:border-[#DADCCD]'}`}>
        <input
          type="checkbox"
          checked={!!ticks[b.id]}
          disabled={disabled}
          onChange={e => onTick(b.id, e.target.checked)}
          className="mt-1 w-4 h-4 accent-[#2F6E14] flex-shrink-0" />
        <div className="min-w-0">
          <div className="font-['Metropolis'] text-[14px] font-bold text-ink leading-snug">{b.summary}</div>
          {b.full && <div className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-snug mt-1 whitespace-pre-wrap">{b.full}</div>}
        </div>
      </label>
    );
  }

  return null;
}

export default FormBlock;
